import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getDetailEvent, postCheckBookedDate } from "../services/apiService";
import WebsiteLayout from "../layouts/WebsiteLayout";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorMessage from "../components/common/ErrorMessage";
import BackNavButton from "../components/common/BackNavButton";
import { isApiSuccess, pickAddress, pickImageUrl, pickTitle, resolvePublicAssetUrl } from "../utils/clubDetailHelpers";
import { pickBottleDayPricing, toBottleOptionLabel } from "../utils/bottleServiceHelpers.js";
import "./BottleDetailPage.css";
import "./BottleAvailabilityPage.css";

function toYmd(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function toMonthLabel(date) {
  return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

function toShortDateLabel(ymd) {
  if (!ymd) return "";
  const [y, m, d] = String(ymd).split("-").map(Number);
  if (!y || !m || !d) return "";
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function toSelectedDateLabel(ymd) {
  if (!ymd) return "";
  const [y, m, d] = String(ymd).split("-").map(Number);
  if (!y || !m || !d) return "";
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    weekday: "long",
  });
}

function bottleOptionId(item, idx) {
  return String(item?.bottleServiceId || item?._id || item?.id || `${idx}`);
}

function toBottleEntries(options) {
  return (options || []).map((item, idx) => ({
    id: bottleOptionId(item, idx),
    item,
  }));
}

function pickBottleName(item) {
  return item?.name || item?.title || item?.bottleName || item?.productName || item?.label || "Bottle";
}

function pickBottleImage(item) {
  const raw = item?.image || item?.imageUrl || item?.photo || item?.thumbnail || item?.icon || item?.picture || "";
  if (typeof raw === "string" && raw.trim()) return resolvePublicAssetUrl(raw.trim());
  return pickImageUrl(item || {});
}

function toFiniteNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "string") {
    const cleaned = value.replace(/[^0-9.-]/g, "");
    if (!cleaned) return null;
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function getDefaultReservationPrice(event) {
  const raw =
    event?.bottleService ||
    event?.bottleServices ||
    event?.bottleMenu ||
    event?.menu ||
    event?.bottles ||
    null;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return 0;
  return (
    toFiniteNumber(
      raw?.reservationPrice ??
        raw?.reservation_price ??
        raw?.reservationAmount ??
        raw?.reservation_amount ??
        raw?.reservationFee
    ) ?? 0
  );
}

function buildBottleCatalog(event) {
  const raw = event?.bottleService || event?.bottleServices || event?.bottleMenu || event?.menu || event?.bottles || {};
  const toList = (value) => (Array.isArray(value) ? value : []);
  const direct = {
    alcoholic: toList(raw?.alcoholic),
    nonAlcoholic: toList(raw?.nonAlcoholic),
  };
  if (direct.alcoholic.length || direct.nonAlcoholic.length) return direct;
  const list = Array.isArray(raw) ? raw : [];
  const alcoholic = [];
  const nonAlcoholic = [];
  list.forEach((item) => {
    const group = String(item?.category || item?.type || item?.group || item?.section || "").toLowerCase();
    if (group.includes("non")) nonAlcoholic.push(item);
    else alcoholic.push(item);
  });
  return { alcoholic, nonAlcoholic };
}

function createEmptySelectionRow() {
  return { selectedId: "" };
}

function hasSelectedBottles(rows) {
  return (rows || []).some((row) => String(row?.selectedId || "").trim());
}

function getSelectableOptions(entries, rows, rowIdx) {
  const selectedByOthers = new Set(
    rows
      .map((row, idx) => (idx === rowIdx ? null : String(row?.selectedId || "")))
      .filter(Boolean)
  );
  return entries.filter((entry) => !selectedByOthers.has(entry.id));
}

function getUnusedOptions(entries, rows) {
  const selected = new Set(rows.map((row) => String(row?.selectedId || "")).filter(Boolean));
  return entries.filter((entry) => !selected.has(entry.id));
}

function normalizeBottleErrorMessage(message) {
  const text = String(message || "").trim();
  if (!text) return "";
  if (/^no bottle.*select date/i.test(text)) {
    return "No bottle available. Select date.";
  }
  return text;
}

export default function BottleAvailabilityPage() {
  const { eventId = "" } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const hostFromUrl = (searchParams.get("host") || "").trim();
  const fromEventUrl = searchParams.get("fromEvent") === "1";
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [checking, setChecking] = useState(false);
  const [checkError, setCheckError] = useState("");
  const [availabilityMeta, setAvailabilityMeta] = useState("");
  const [selectedAlcoholic, setSelectedAlcoholic] = useState([]);
  const [selectedNonAlcoholic, setSelectedNonAlcoholic] = useState([]);
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getDetailEvent(eventId);
        if (cancelled) return;
        if (!isApiSuccess(res?.status) || !res?.data) {
          setError(res?.message || "Bottle detail not found");
          setEvent(null);
          return;
        }
        setEvent(res.data);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Error");
          setEvent(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  const title = pickTitle(event || {});
  const address = pickAddress(event || {});
  const defaultReservationPrice = useMemo(() => getDefaultReservationPrice(event), [event]);
  const bottleCatalog = useMemo(() => buildBottleCatalog(event), [event]);
  const alcoholicEntries = useMemo(() => toBottleEntries(bottleCatalog.alcoholic || []), [bottleCatalog.alcoholic]);
  const nonAlcoholicEntries = useMemo(() => toBottleEntries(bottleCatalog.nonAlcoholic || []), [bottleCatalog.nonAlcoholic]);
  const todayYmd = toYmd(new Date());
  const calendarCells = useMemo(() => {
    const first = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1);
    const dayOffset = first.getDay();
    const lastDay = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < dayOffset; i += 1) cells.push(null);
    for (let d = 1; d <= lastDay; d += 1) cells.push(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [calendarMonth]);

  useEffect(() => {
    setSelectedAlcoholic(alcoholicEntries.length ? [createEmptySelectionRow()] : []);
    setSelectedNonAlcoholic(nonAlcoholicEntries.length ? [createEmptySelectionRow()] : []);
  }, [alcoholicEntries, nonAlcoholicEntries]);

  const updateRow = (setter, rowIdx, patch) => {
    setCheckError("");
    setter((prev) =>
      prev.map((row, idx) => (idx === rowIdx ? { ...row, ...patch } : row))
    );
  };
  const addRow = (setter, entries) => {
    setCheckError("");
    setter((prev) => {
      const available = getUnusedOptions(entries, prev);
      if (!available.length) return prev;
      return [...prev, createEmptySelectionRow()];
    });
  };
  const removeRow = (setter, rowIdx) => {
    setCheckError("");
    setter((prev) => (prev.length <= 1 ? prev : prev.filter((_, idx) => idx !== rowIdx)));
  };

  const buildPayloadItems = (rows, entries) => {
    const map = new Map(entries.map((entry) => [entry.id, entry.item]));
    return rows
      .map((row) => {
        const item = map.get(String(row.selectedId || ""));
        if (!item) return null;
        const { bottlePrice } = pickBottleDayPricing(item, selectedDate, defaultReservationPrice);
        return {
          image: pickBottleImage(item),
          name: pickBottleName(item),
          bottleServiceId: String(item?.bottleServiceId || item?._id || item?.id || ""),
          bottlePrice,
          price: bottlePrice,
          quantity: 1,
          qunatity: "1",
        };
      })
      .filter(Boolean);
  };

  const buildAvailabilityPayload = useCallback(() => {
    if (!selectedDate) {
      return { error: "Please select date." };
    }
    const alcoholic = buildPayloadItems(selectedAlcoholic, alcoholicEntries);
    const nonAlcoholic = buildPayloadItems(selectedNonAlcoholic, nonAlcoholicEntries);
    const bottleServiceId = [...alcoholic, ...nonAlcoholic]
      .map((item) => String(item?.bottleServiceId || "").trim())
      .filter(Boolean);
    if (!alcoholic.length && !nonAlcoholic.length) {
      return { error: "Please select at least one bottle." };
    }
    return {
      payload: {
        eventId,
        startDate: selectedDate,
        endDate: selectedDate,
        isTable: "no",
        type: "BottleService",
        alcoholic,
        nonAlcoholic,
        bottleServiceId,
      },
    };
  }, [selectedDate, selectedAlcoholic, selectedNonAlcoholic, alcoholicEntries, nonAlcoholicEntries, eventId]);

  const runAvailabilityCheck = useCallback(async (showLoading = true, options = {}) => {
    const validateSelection = options.validateSelection !== false;
    const built = buildAvailabilityPayload();
    if (built?.error) {
      setCheckError(built.error);
      if (validateSelection) {
        setAvailabilityMeta("");
      }
      return false;
    }
    if (!built?.payload) return false;
    setCheckError("");
    if (showLoading) setChecking(true);
    try {
      const res = await postCheckBookedDate(built.payload);
      if (!isApiSuccess(res?.status)) {
        setCheckError(res?.message || "Bottle service not available.");
        return false;
      }
      setAvailabilityMeta("");
      return true;
    } catch (e) {
      setCheckError(e?.message || "Failed to check availability.");
      return false;
    } finally {
      if (showLoading) setChecking(false);
    }
  }, [buildAvailabilityPayload]);

  const hasAlcoholicSelection = useMemo(() => hasSelectedBottles(selectedAlcoholic), [selectedAlcoholic]);
  const hasNonAlcoholicSelection = useMemo(
    () => hasSelectedBottles(selectedNonAlcoholic),
    [selectedNonAlcoholic]
  );
  const hasBottleSelection = hasAlcoholicSelection || hasNonAlcoholicSelection;
  const isCalendarDateEnabled = useCallback(
    (ymd) => Boolean(ymd && ymd >= todayYmd),
    [todayYmd]
  );

  const disableBottleSelection = checking;
  const disableNext = checking || !selectedDate || !hasBottleSelection;

  const handleCheckBottleAvailability = async () => {
    setChecking(true);
    const ok = await runAvailabilityCheck(true);
    setChecking(false);
    if (!ok) return;
    const built = buildAvailabilityPayload();
    if (!built?.payload) return;
    const query = new URLSearchParams();
    if (hostFromUrl) query.set("host", hostFromUrl);
    if (fromEventUrl) query.set("fromEvent", "1");
    const qs = query.toString();
    navigate(`/bottle/${encodeURIComponent(eventId)}/review${qs ? `?${qs}` : ""}`, {
      state: {
        event,
        bookingInput: {
          eventId,
          hostId: hostFromUrl || event?.hostId?._id || event?.hostId || event?.userId?._id || event?.userId || "",
          selectedDate,
          selectedEndDate: selectedDate,
          days: 1,
          alcoholic: built.payload.alcoholic || [],
          nonAlcoholic: built.payload.nonAlcoholic || [],
          bottleServiceId: built.payload.bottleServiceId || [],
          isTable: "no",
          type: "BottleService",
        },
      },
    });
  };

  if (!eventId) {
    return (
      <WebsiteLayout>
        <ErrorMessage message="Missing bottle id." />
      </WebsiteLayout>
    );
  }
  if (loading) {
    return (
      <WebsiteLayout>
        <LoadingSpinner label="Loading availability..." />
      </WebsiteLayout>
    );
  }
  if (error || !event) {
    return (
      <WebsiteLayout>
        <ErrorMessage message={error || "Not found"} />
      </WebsiteLayout>
    );
  }

  return (
    <WebsiteLayout>
      <div className="bottle-page">
        <div className="bottle-content" style={{ marginTop: 0 }}>
          <div className="bottle-inner">
            <div className="bottle-availability-head">
              <BackNavButton
                to={
                  fromEventUrl
                    ? `/club/event/${encodeURIComponent(eventId)}`
                    : hostFromUrl
                      ? `/bottle/${encodeURIComponent(eventId)}?host=${encodeURIComponent(hostFromUrl)}`
                      : `/bottle/${encodeURIComponent(eventId)}`
                }
                ariaLabel="Back"
              />
              <h1 className="bottle-title bottle-title-sm">Select Date</h1>
            </div>
            <p className="bottle-meta-line">{address ? `📍 ${address}` : title}</p>

            <section className="bottle-section">
              <div className="bottle-calendar">
                <div className="bottle-calendar-top">
                  <strong>{toMonthLabel(calendarMonth)}</strong>
                  <div>
                    <button type="button" onClick={() => setCalendarMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))}>
                      {"<"}
                    </button>
                    <button type="button" onClick={() => setCalendarMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))}>
                      {">"}
                    </button>
                  </div>
                </div>
                <div className="bottle-calendar-grid">
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                    <div key={d} className="bottle-weekday">
                      {d}
                    </div>
                  ))}
                  {calendarCells.map((cell, idx) => {
                    if (!cell) return <div key={`empty-${idx}`} className="bottle-day empty" />;
                    const ymd = toYmd(cell);
                    const disabled = !isCalendarDateEnabled(ymd);
                    const active = selectedDate === ymd;
                    return (
                      <button
                        key={ymd}
                        type="button"
                        disabled={disabled}
                        className={`bottle-day ${active ? "active" : ""} ${disabled ? "unavailable" : ""}`}
                        onClick={() => {
                          setSelectedDate(ymd);
                          setCheckError("");
                          setAvailabilityMeta("");
                        }}
                      >
                        {cell.getDate()}
                      </button>
                    );
                  })}
                </div>
              </div>
              {checkError ? <p className="bottle-availability-error">{normalizeBottleErrorMessage(checkError)}</p> : null}
              {selectedDate ? (
                <div className="bottle-availability-card bottle-availability-card--selected">
                  <p className="bottle-availability-title">Selected Date</p>
                  <p className="bottle-selected-date-label">{toSelectedDateLabel(selectedDate)}</p>
                  {!hasBottleSelection ? (
                    <p className="bottle-selected-date-hint">Select bottle(s) below to continue.</p>
                  ) : null}
                </div>
              ) : (
                <p className="bottle-selected-date-hint">Pick a date from the calendar above.</p>
              )}
            </section>

            <section className="bottle-section">
              <h2 className="bottle-section-title">Bottle Service</h2>
              <div className="bottle-group-card">
                <h3 className="bottle-group-title">Alcoholic</h3>
                {selectedAlcoholic.map((row, idx) => (
                  <div key={`alc-${idx}`} className="bottle-picker-row">
                    <label className="bottle-picker-label">{`Bottle ${idx + 1}`}</label>
                    <div className="bottle-picker-control-row">
                      {(() => {
                        const options = getSelectableOptions(alcoholicEntries, selectedAlcoholic, idx);
                        return (
                      <select
                        className="bottle-select"
                        value={row.selectedId}
                        disabled={disableBottleSelection}
                        onChange={(e) => updateRow(setSelectedAlcoholic, idx, { selectedId: e.target.value })}
                      >
                        <option value="">Select bottle</option>
                        {options.map((entry) => {
                          return (
                            <option key={entry.id} value={entry.id}>
                              {toBottleOptionLabel(entry.item, selectedDate, defaultReservationPrice)}
                            </option>
                          );
                        })}
                      </select>
                        );
                      })()}
                      {idx > 0 ? (
                        <button
                          type="button"
                          className="bottle-remove-btn"
                          disabled={disableBottleSelection}
                          onClick={() => removeRow(setSelectedAlcoholic, idx)}
                        >
                          Remove
                        </button>
                      ) : null}
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  className="bottle-add-btn"
                  onClick={() => {
                    addRow(setSelectedAlcoholic, alcoholicEntries);
                  }}
                  disabled={disableBottleSelection || !getUnusedOptions(alcoholicEntries, selectedAlcoholic).length}
                  style={{ display: getUnusedOptions(alcoholicEntries, selectedAlcoholic).length ? undefined : "none" }}
                >
                  Add another alcoholic bottle
                </button>
              </div>

              <div className="bottle-group-card">
                <h3 className="bottle-group-title">Non-Alcoholic</h3>
                {selectedNonAlcoholic.map((row, idx) => (
                  <div key={`non-${idx}`} className="bottle-picker-row">
                    <label className="bottle-picker-label">{`Bottle ${idx + 1}`}</label>
                    <div className="bottle-picker-control-row">
                      {(() => {
                        const options = getSelectableOptions(nonAlcoholicEntries, selectedNonAlcoholic, idx);
                        return (
                      <select
                        className="bottle-select"
                        value={row.selectedId}
                        disabled={disableBottleSelection}
                        onChange={(e) => updateRow(setSelectedNonAlcoholic, idx, { selectedId: e.target.value })}
                      >
                        <option value="">Select bottle</option>
                        {options.map((entry) => {
                          return (
                            <option key={entry.id} value={entry.id}>
                              {toBottleOptionLabel(entry.item, selectedDate, defaultReservationPrice)}
                            </option>
                          );
                        })}
                      </select>
                        );
                      })()}
                      {idx > 0 ? (
                        <button
                          type="button"
                          className="bottle-remove-btn"
                          disabled={disableBottleSelection}
                          onClick={() => removeRow(setSelectedNonAlcoholic, idx)}
                        >
                          Remove
                        </button>
                      ) : null}
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  className="bottle-add-btn"
                  onClick={() => {
                    addRow(setSelectedNonAlcoholic, nonAlcoholicEntries);
                  }}
                  disabled={disableBottleSelection || !getUnusedOptions(nonAlcoholicEntries, selectedNonAlcoholic).length}
                  style={{ display: getUnusedOptions(nonAlcoholicEntries, selectedNonAlcoholic).length ? undefined : "none" }}
                >
                  Add another non-alcoholic bottle
                </button>
              </div>
            </section>

            <div className="bottle-bottom-action">
              <button
                type="button"
                className="bottle-next-btn"
                onClick={handleCheckBottleAvailability}
                disabled={disableNext}
              >
                {checking ? "Checking..." : "Next"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </WebsiteLayout>
  );
}
